import React, { FC, useEffect, useState } from "react";
import type { FormInstance, FormProps } from "antd";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
  TimePicker,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { IReferralLetter } from "../../../interfaces/IReferral";
import { IDocumentFields } from "../../../interfaces/ICase";
import { formattedDateToISOStringWithDateIncrement } from "../../../utils/globalUtils";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

const initialValues: FieldType = {
  beneficiaryId: "",
  name: "",
  age: "",
  refferenceID: null,
  dateIssued: "",
  tokenNumber: "",
  gender: "",
  timeIssued: "",
  referralBy: "",
  remark: "",
  referralNo: "",
  referralExpiryFrom: null,
  referralExpiryTo: null,
  noOfVisits: "",
  referringDoctor: "",
  referralFor: "",
  referralType: "",
  cardCategory: "",
  cardNumber: "",
  serviceNumber: "",
  ariAndSC: false,
  clinicalNotes: "",
  admission: "",
  consultationFor: "",
  investigation: "",
  ipAllowed: false,
  polyClinic: null,
  polyclinicRemarks: "",
};

type ComponentDetail = {
  id: number;
  name: string;
  validUpto: string; // Adjust type as needed
  remarks?: string;
  maxQty?: string;
  referralType: string;
};

type PatientReferralProps = {
  fetchReferralDetail: IReferralLetter | null;
  showCompareModal?: boolean;
  listOfResponseUrl?: Array<IDocumentFields> | null;
  handleShowServices?: CallableFunction;
  showServices?: boolean;
  form: any;
  setServices: CallableFunction;
  setServicesManually: CallableFunction;
  services: ComponentDetail[];
  servicesManually: ComponentDetail[];
  setPatientReferralForm: React.Dispatch<
    React.SetStateAction<FormInstance | null>
  >;
  schemeSelected: string;
  isDisabled?: boolean;
};
type FieldType = {
  beneficiaryId?: string;
  name?: string | null;
  age?: string | null;
  refferenceID?: string | null;
  dateIssued?: string;
  tokenNumber?: string | null;
  gender?: string | null;
  timeIssued?: string | null;
  referralBy?: string | null;
  remark?: string | null;
  referralNo?: string;
  referralExpiryFrom?: string | null;
  referralExpiryTo?: string | null;
  noOfVisits?: string | null;
  referringDoctor?: string;
  referralFor?: string | null;
  referralType?: string | null;
  cardCategory?: string | null;
  cardNumber?: string;
  serviceNumber?: string;
  ariAndSC?: boolean;
  clinicalNotes?: string | null;
  admission?: string | null;
  consultationFor?: string | null;
  investigation?: string | null;
  ipAllowed?: boolean;
  polyClinic?: string | null;
  consumedVisits?: any;
  polyclinicRemarks?: any;
};

const PatientReferral: FC<PatientReferralProps> = ({
  fetchReferralDetail,
  showCompareModal,
  listOfResponseUrl,
  handleShowServices,
  showServices,
  setServices,
  setServicesManually,
  servicesManually,
  services,
  setPatientReferralForm,
  schemeSelected,
  isDisabled
}) => {
  const [form] = Form.useForm();
  const dateFormat = "DD-MM-YYYY";
  const { Option } = Select;

  const handleAddService = () => {
    const newService = {
      referralType: "",
      name: "",
      remarks: "",
      validUpto: "",
      maxQty: "",
    };

    setServicesState((prevServices: any) => [...prevServices, newService]);

    // Also update the form fields for the new service
    const currentServices = form.getFieldValue("services") || [];
    form.setFieldsValue({
      services: [...currentServices, newService],
    });
  };

  // Handler to remove a service
  const handleRemoveService = (index: any) => {
    setServicesState((prevServices) =>
      prevServices.filter((_, i) => i !== index)
    );

    const currentServices = form.getFieldValue("services") || [];
    const updatedServices = currentServices?.filter(
      (_: any, i: any) => i !== index
    );
    form.setFieldsValue({
      services: updatedServices,
    });
  };

  useEffect(() => {
    setPatientReferralForm(form);
  }, [form, setPatientReferralForm]);

  //   const onFinish: FormProps<FieldType>["onFinish"] = (values: any) => {
  //     const stringTimeIssued = dayjs(values?.timeIssued).format("hh:mm:ss");

  //     const data = services.map((item, index) => ({
  //       id: item?.id,
  //       name: item.name,
  //       validUpto: dayjs(values[`services[${index}].validUpto`]).format(
  //         "DD-MM-YYYY"
  //       ),
  //       remarks: values[`services[${index}].remarks`] || item.remarks || "",
  //       maxQty: item.maxQty || "NA",
  //       referralType: item.referralType || "",
  //     }));

  //     const updatedValues = {
  //       ...values,
  //       dateIssued: formattedDateToISOStringWithDateIncrement(values?.dateIssued),
  //       timeIssued: stringTimeIssued,
  //       services: data,
  //     };

  //     // handleShowConfirmPopUp(updatedValues, "cghs", fetchReferralDetail?.id);
  //   };

  // useEffect(() => {
  //   if (!listOfResponseUrl?.documentUrl) {
  //     form.resetFields();
  //     setServices([]);
  //   }
  // }, [listOfResponseUrl?.documentUrl]);

  useEffect(() => {
    if (!listOfResponseUrl || listOfResponseUrl?.length === 0) {
      form.resetFields();
      setServices([]);
    }
  }, [listOfResponseUrl]);

  useEffect(() => {
    if (fetchReferralDetail) {
      const updatedValues = {
        beneficiaryId: fetchReferralDetail?.beneficiaryId,
        name: fetchReferralDetail?.name,
        age: fetchReferralDetail?.age,
        refferenceID: fetchReferralDetail?.refferenceID,
        remark: fetchReferralDetail?.remark,
        referralBy:
          fetchReferralDetail?.doctorName || fetchReferralDetail?.referralBy,
        dateIssued:
          fetchReferralDetail?.dateIssued &&
          fetchReferralDetail?.dateIssued !== "" &&
          dayjs(fetchReferralDetail?.dateIssued, dateFormat, true).isValid()
            ? dayjs(fetchReferralDetail?.dateIssued, dateFormat)
            : null,
        tokenNumber: fetchReferralDetail?.tokenNumber,
        gender: fetchReferralDetail?.gender,
        timeIssued:
          fetchReferralDetail?.timeIssued &&
          fetchReferralDetail?.timeIssued !== ""
            ? dayjs(fetchReferralDetail?.timeIssued, "hh:mm:ss")
            : null,
        referralNo: fetchReferralDetail?.referralNo,
        referralExpiryFrom:
          fetchReferralDetail?.referralExpiryFrom &&
          dayjs(
            fetchReferralDetail?.referralExpiryFrom,
            dateFormat,
            true
          ).isValid()
            ? dayjs(fetchReferralDetail?.referralExpiryFrom, dateFormat)
            : null,
        referralExpiryTo:
          fetchReferralDetail?.referralExpiryTo &&
          dayjs(
            fetchReferralDetail?.referralExpiryTo,
            dateFormat,
            true
          ).isValid()
            ? dayjs(fetchReferralDetail?.referralExpiryTo, dateFormat)
            : null,
        noOfVisits: fetchReferralDetail?.noOfVisits || "",
        referringDoctor: fetchReferralDetail?.referringDoctor || "",
        referralFor: fetchReferralDetail?.referralFor || "",
        referralType: fetchReferralDetail?.referralType || "",
        cardCategory: fetchReferralDetail?.cardCategory || "",
        cardNumber: fetchReferralDetail?.cardNumber || "",
        serviceNumber: fetchReferralDetail?.serviceNumber || "",
        ariAndSC: fetchReferralDetail?.ariAndSC || false,
        clinicalNotes: fetchReferralDetail?.clinicalNotes || "",
        admission: fetchReferralDetail?.admission || "",
        consultationFor: fetchReferralDetail?.consultationFor || "",
        investigation: fetchReferralDetail?.investigation || "",
        ipAllowed: fetchReferralDetail?.ipAllowed || false,
        polyClinic: fetchReferralDetail?.polyClinic || "",
        consumedVisits: fetchReferralDetail?.consumedVisits || "",
        polyclinicRemarks: fetchReferralDetail?.polyclinicRemarks || "",
      };
      form.setFieldsValue(updatedValues);
      form.validateFields();
      const servicesData = constructServices(fetchReferralDetail);
      setServices(servicesData);
    }
  }, [fetchReferralDetail, form]);

  const [servicesState, setServicesState] = useState(services);
  const [servicesStateManually, setServicesStateManually] =
    useState(servicesManually);

  useEffect(() => {
    setServicesState(services);
  }, [services]);

  const handleServiceChange = (index: any, field: any, value: any) => {
    const updatedServices = [...servicesState];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServicesState(updatedServices);
    setServices(updatedServices); // Update the parent component's state
  };
  const handleServiceChange2 = (index: any, field: any, value: any) => {
    const updatedServices = [...servicesStateManually];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServicesStateManually(updatedServices);
    setServicesManually(updatedServices);
  };

  const handleAddScheme = () => {
    const newId =
      servicesManually.length > 0 ? servicesManually?.length + 1 : 0;

    const newData = {
      id: newId,
      referralType: "",
      name: "",
      remarks: "",
      validUpto: "",
      maxQty: "NA",
    };

    // Update the state by adding the new data
    setServicesManually((prevState: any) => {
      return [...prevState, newData];
    });
    setServicesStateManually((prevState: any) => {
      return [...prevState, newData];
    });
  };

  return (
    <>
      {/* <Divider
        orientation="left"
        style={{
          borderColor: "#bcbcbc",
          // fontSize: "20px",
          fontWeight: "500",
          padding: " 0px 10px",
          color: " rgb(166, 166, 166)",
          fontSize: "1rem",
        }}
      >
        REFERRAL DETAILS
      </Divider>

      {schemeSelected?.toLowerCase()?.includes("cghs") ? (
        <Form
          form={form}
          name="patient-details-form"
          initialValues={initialValues}
          // onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Row justify="space-between"></Row>
          <Row justify="space-between">
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 4}
              lg={showCompareModal ? 24 : 4}
              xl={showCompareModal ? 24 : 4}
            >
              <Form.Item<FieldType>
                label="REFERRAL ID"
                name="refferenceID"
                rules={[
                  { required: true, message: "Please input referral id!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 7}
              lg={showCompareModal ? 24 : 7}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="REFERRAL BY"
                name="referralBy"
                rules={[
                  {
                    required: true,
                    message: "Please input referral by doctor name!",
                  },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 2}
              lg={showCompareModal ? 24 : 2}
              xl={showCompareModal ? 24 : 2}
            >
              <Form.Item<FieldType> label="TOKEN NO" name="tokenNumber">
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 2}
              lg={showCompareModal ? 24 : 2}
              xl={showCompareModal ? 24 : 2}
            >
              <Form.Item<FieldType>
                label="DATE OF ISSUE"
                name="dateIssued"
                rules={[
                  {
                    required: true,
                    message: "Please input date of issue!",
                  },
                ]}
              >
                <DatePicker
                  defaultValue={
                    fetchReferralDetail?.dateIssued &&
                    fetchReferralDetail?.dateIssued !== ""
                      ? dayjs(fetchReferralDetail?.dateIssued, dateFormat)
                      : null
                  }
                  format={dateFormat}
                  style={{ width: "100%" }}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 2}
              lg={showCompareModal ? 24 : 2}
              xl={showCompareModal ? 24 : 2}
            >
              <Form.Item<FieldType> label="TIME OF ISSUE" name="timeIssued">
                <TimePicker
                  use12Hours
                  format="hh:mm:ss"
                  style={{ width: "100%" }}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 6}
              lg={showCompareModal ? 24 : 6}
              xl={showCompareModal ? 24 : 6}
            >
              <Form.Item<FieldType> label="OVERALL REMARKS" name="remark">
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 24}
              lg={showCompareModal ? 24 : 24}
              xl={showCompareModal ? 24 : 24}
              style={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
              }}
            >
              {!showServices && services?.length > 0 && (
                <div
                  style={{
                    color: "#4a4a98",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                  }}
                  onClick={(e) => {
                    if (handleShowServices) {
                      handleShowServices();
                    }
                  }}
                >
                  ...SEE MORE
                </div>
              )}
            </Col>
          </Row>

          {servicesState &&
            servicesState?.length > 0 &&
            showServices &&
            servicesState?.map((item, index) => (
              <Row
                gutter={[8, 4]}
                key={index}
                style={{
                  border: "1px solid lightgray",
                  borderRadius: "16px",
                  padding: "4px",
                  marginBottom: "0.8rem",
                  width: "100%",
                }}
              >
                <Col span={0.5} style={{ marginTop: "30px" }}>
                  <span
                    style={{
                      background: "#00549E",
                      color: "white",
                      borderRadius: "50%",
                      padding: "0 4px",
                      fontSize: "12px",
                      height: "20px",
                      width: "20px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {index + 1}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={showCompareModal ? 24 : 3}
                  lg={showCompareModal ? 24 : 3}
                  xl={showCompareModal ? 24 : 3}
                >
                  <Form.Item
                    name={["services", index, "referralType"]}
                    label="REFERRAL TYPE"
                    initialValue={item.referralType}
                  >
                    <Input
                    disabled={isDisabled}
                      value={item.referralType}
                      onChange={(e) =>
                        handleServiceChange(
                          index,
                          "referralType",
                          e.target.value
                        )
                      }
                      style={{border: !item?.referralType ? '1px solid red' : ''}}
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={showCompareModal ? 24 : 8}
                  lg={showCompareModal ? 24 : 8}
                  xl={showCompareModal ? 24 : 8}
                >
                  <Form.Item
                    name={["services", index, "name"]}
                    label="COMPONENT DETAILS"
                  >
                    <Tooltip title={item.name} placement="topLeft">
                      <Input
                      disabled={isDisabled}
                        value={item.name}
                        onChange={(e) =>
                          handleServiceChange(index, "name", e.target.value)
                        }
                         style={{border: !item?.name ? '1px solid red' : ''}}
                      />
                    </Tooltip>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={showCompareModal ? 24 : 7}
                  lg={showCompareModal ? 24 : 7}
                  xl={showCompareModal ? 24 : 7}
                >
                  <Form.Item
                    name={["services", index, "remarks"]}
                    label="REMARKS"
                  >
                    <Tooltip title={item.remarks} placement="topLeft">
                      <Input
                      disabled={isDisabled}
                        value={item.remarks}
                        onChange={(e) =>
                          handleServiceChange(index, "remarks", e.target.value)
                        }
                         style={{border: !item?.remarks ? '1px solid red' : ''}}
                      />
                    </Tooltip>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={showCompareModal ? 24 : 2}
                  lg={showCompareModal ? 24 : 2}
                  xl={showCompareModal ? 24 : 2}
                >
                  <Form.Item
                    name={["services", index, "validUpto"]}
                    label="VALID UPTO"
                    initialValue={
                      item?.validUpto &&
                      item?.validUpto !== "" &&
                      dayjs(item?.validUpto, dateFormat, true).isValid()
                        ? dayjs(item?.validUpto, dateFormat)
                        : null
                    }
                  >
                    <DatePicker
                    disabled={isDisabled}
                      value={
                        item.validUpto &&
                        item.validUpto !== "" &&
                        dayjs(item?.validUpto, dateFormat, true).isValid()
                          ? dayjs(item.validUpto, dateFormat)
                          : null
                      }
                      onChange={(date) =>
                        handleServiceChange(
                          index,
                          "validUpto",
                          date ? date.format(dateFormat) : ""
                        )
                      }
                      format={dateFormat}
                      style={{ width: "100%", border: !item?.validUpto ? '1px solid red' : '' }}
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={showCompareModal ? 24 : 2}
                  lg={showCompareModal ? 24 : 2}
                  xl={showCompareModal ? 24 : 2}
                >
                  <Form.Item
                    name={["services", index, "maxQty"]}
                    label="MAX QUANTITY"
                    initialValue={item.maxQty}
                  >
                    <Input
                    disabled={isDisabled}
                      value={item.maxQty}
                      onChange={(e) =>
                        handleServiceChange(index, "maxQty", e.target.value)
                      }
                       style={{border: !item?.maxQty ? '1px solid red' : ''}}
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={showCompareModal ? 24 : 1}
                  lg={showCompareModal ? 24 : 1}
                  xl={showCompareModal ? 24 : 1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // marginTop: "30px",
                  }}
                >
                  {servicesState?.length > 1 && (
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => handleRemoveService(index)}
                      style={{
                        border: "none",
                        boxShadow: "none",
                      }}
                    />
                  )}
                </Col>
              </Row>
            ))}

          {showServices && (
            <Row style={{ marginBottom: "1rem" }}>
              <Col span={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    type="dashed"
                    onClick={handleAddService}
                    icon={<PlusOutlined />}
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                      width: "20%",
                    }}
                  >
                    Add Service
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </Form>
      ) : (
        <Form
          form={form}
          name="patient-details-form"
          initialValues={initialValues}
          // onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Row justify="space-between">
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="REFERRAL NO "
                name="referralNo"
                rules={[
                  { required: true, message: "Please input referral no!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="POLYCLINIC "
                name="polyClinic"
                rules={[
                  { required: true, message: "Please input polyclinic!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="DATE OF ISSUE"
                name="referralExpiryFrom"
                rules={[
                  {
                    required: true,
                    message: "Please input date of issue!",
                  },
                ]}
              >
                <DatePicker
                  defaultValue={
                    fetchReferralDetail?.referralExpiryFrom
                      ? dayjs(
                          fetchReferralDetail?.referralExpiryFrom,
                          dateFormat
                        )
                      : null
                  }
                  format={dateFormat}
                  width={"100%"}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="VALID UPTO"
                name="referralExpiryTo"
                rules={[
                  {
                    required: true,
                    message: "Please input referral expiry date!",
                  },
                ]}
              >
                <DatePicker
                  defaultValue={
                    fetchReferralDetail?.referralExpiryTo
                      ? dayjs(fetchReferralDetail?.referralExpiryTo, dateFormat)
                      : null
                  }
                  format={dateFormat}
                  width={"100%"}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="CONSUMED VISITS"
                name="consumedVisits"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please input no. of visits allowed!",
                //   },
                // ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="NO OF VISITS ALLOWED"
                name="noOfVisits"
                rules={[
                  {
                    required: true,
                    message: "Please input no. of visits allowed!",
                  },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="REFERRING DOCTOR"
                name="referringDoctor"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please input referring doctor name!",
                //   },
                // ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="REFERRAL FOR"
                name="referralFor"
                rules={[
                  {
                    required: true,
                    message: "Please enter name of referral for!",
                  },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="PATIENT TYPE"
                name="referralType"
                rules={[
                  { required: true, message: "Please input referral type!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="CARD CATEGORY"
                name="cardCategory"
                rules={[
                  { required: true, message: "Please input card category!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="CARD NO"
                name="cardNumber"
                rules={[
                  { required: true, message: "Please input card number!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="SERVICE NO"
                name="serviceNumber"
                rules={[
                  { required: true, message: "Please input service number!" },
                ]}
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType> label="CLINICAL NOTES" name="clinicalNotes">
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType> label="INVESTIGATION" name="investigation">
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="CONSULTATION FOR"
                name="consultationFor"
              >
                <Input disabled={isDisabled}/>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="IS ARI & SC?"
                name="ariAndSC"
                rules={[
                  { required: true, message: "Please select your input!" },
                ]}
              >
                <Radio.Group>
                  <Radio value={true} disabled={isDisabled}>YES</Radio>
                  <Radio value={false} disabled={isDisabled}>NO</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            >
              <Form.Item<FieldType>
                label="IS IP ALLOWED?"
                name="ipAllowed"
                rules={[
                  { required: true, message: "Please select your input!" },
                ]}
              >
                <Radio.Group>
                  <Radio value={true} disabled={isDisabled}>YES</Radio>
                  <Radio value={false} disabled={isDisabled}>NO</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={showCompareModal ? 24 : 11}
              lg={showCompareModal ? 24 : 11}
              xl={showCompareModal ? 24 : 7}
            ></Col>
          </Row>
        </Form>
      )} */}
    </>
  );
};

export default PatientReferral;

const constructServices = (fetchReferralDetail: IReferralLetter | null) => {
  if (!fetchReferralDetail) return [];
  const services =
    fetchReferralDetail?.services ??
    fetchReferralDetail?.referralServicesResponses;

  return services?.map((service: any) => ({
    id: service?.id,
    referralType: service?.referralType,
    name: service?.name,
    remarks: service?.remarks,
    validUpto: service?.validUpto !== "" ? service?.validUpto : null,
    maxQty: service?.maxQty,
  }));
};
